import React from 'react';
import { CreditCard, Banknote, ArrowLeft, MapPin, Tag, X, Check, Gift, Store, Truck, Clock, Plus, ChevronRight, Trash2, Phone, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { formatPrice } from '../../lib/utils';
import StampCard from '../StampCard';

const OrderSummarySection = ({
  addFillerToCart,
  appliedPromo,
  applyPromoCode,
  cart,
  cartTotal,
  checkingDelivery,
  deliveryFee,
  discountAmount,
  fillerItems,
  fillerScrollRef,
  finalTotal,
  getLocalizedAddonName,
  getLocalizedName,
  getTranslatedRemoved,
  isAuthenticated,
  isDrinksOnly,
  loyaltyStatus,
  name,
  orderType,
  promoCode,
  promoLoading,
  removeFromCart,
  removePromoCode,
  scrollFiller,
  selectedReward,
  setPromoCode,
  t,
}) => (
  <>
          <div className="order-1 lg:order-2">
            <Card className="bg-[#141417] border-[#27272A] sticky top-24">
              <CardHeader>
                <CardTitle>{t('checkout.orderSummary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cart.items.map((item) => (
                    <div key={item.menu_item_id} className="flex gap-3" data-testid={`checkout-item-${item.menu_item_id}`}>
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white">
                          {getLocalizedName(item)}
                          {item.selected_size && (
                            <span className="text-xs text-zinc-400 font-normal ml-1">
                              ({item.selected_size === 'big' ? t('itemDetail.sizeBig') : t('itemDetail.sizeNormal')})
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-zinc-400">{t('checkout.qty')}: {item.quantity}</p>
                        {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                          <p className="text-xs text-red-400 mt-1">
                            {t('itemDetail.withoutItems', { items: getTranslatedRemoved(item).join(', ') })}
                          </p>
                        )}
                        {item.has_combo && (
                          <p className="text-xs text-green-400 mt-1" data-testid={`checkout-combo-${item.menu_item_id}`}>
                            {t('itemDetail.comboTitle')} (+CHF 6.00)
                          </p>
                        )}
                        {item.selected_addons?.length > 0 && (
                          <p className="text-xs text-[#FFC107]/70 mt-1" data-testid={`checkout-addons-${item.menu_item_id}`}>
                            {t('itemDetail.extras')}: {item.selected_addons.map(a => `${getLocalizedAddonName(a)} (+CHF ${a.price.toFixed(2)})`).join(', ')}
                          </p>
                        )}
                        {item.special_instructions && (
                          <p className="text-xs text-zinc-500 mt-1 italic">"{item.special_instructions}"</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <p className="font-semibold text-[#FFC107]">
                          {formatPrice((((item.selected_size === 'big' && item.price_big) ? item.price_big : item.price) + (item.has_combo ? 6.0 : 0) + (item.selected_addons || []).reduce((s,a) => s + a.price, 0)) * item.quantity)}
                        </p>
                        <button
                          onClick={() => removeFromCart(item.menu_item_id)}
                          className="p-1 text-zinc-500 hover:text-red-400 transition-colors"
                          data-testid={`remove-item-${item.menu_item_id}`}
                          aria-label="Remove item"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Cart Filler - Quick Add Drinks & Appetizers */}
                {fillerItems.length > 0 && (
                  <div className="mt-5 mb-2" data-testid="cart-filler-section">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-zinc-400">{t('checkout.addSomethingExtra')}</p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => scrollFiller(-1)}
                          className="w-6 h-6 flex items-center justify-center rounded-full border border-zinc-700 text-zinc-400 hover:border-[#FFC107]/50 hover:text-[#FFC107] transition-colors"
                          data-testid="filler-scroll-left"
                          aria-label="Scroll left"
                        >
                          <ChevronRight className="h-3.5 w-3.5 rotate-180" />
                        </button>
                        <button
                          onClick={() => scrollFiller(1)}
                          className="w-6 h-6 flex items-center justify-center rounded-full border border-zinc-700 text-zinc-400 hover:border-[#FFC107]/50 hover:text-[#FFC107] transition-colors"
                          data-testid="filler-scroll-right"
                          aria-label="Scroll right"
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="relative">
                      <div ref={fillerScrollRef} className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide scroll-smooth">
                        {fillerItems.map(item => (
                          <button
                            key={item.id}
                            onClick={() => addFillerToCart(item)}
                            className="flex-shrink-0 w-[100px] bg-[#141417]/60 border border-yellow-500/10 hover:border-[#FFC107]/40 rounded-lg p-1.5 transition-all duration-200 hover:bg-[#25252E]/60 group text-center"
                            data-testid={`filler-item-${item.id}`}
                          >
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-14 rounded-md object-cover mb-1.5"
                            />
                            <p className="text-[10px] font-medium text-white leading-tight truncate">{item.name}</p>
                            <div className="flex items-center justify-center gap-1 mt-1">
                              <span className="text-[10px] font-semibold text-[#FFC107]">{formatPrice(item.price)}</span>
                              <Plus className="h-3 w-3 text-zinc-500 group-hover:text-[#FFC107] transition-colors" />
                            </div>
                          </button>
                        ))}
                        <div className="flex-shrink-0 w-1" aria-hidden="true" />
                      </div>
                      <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-zinc-900 to-transparent pointer-events-none" />
                    </div>
                  </div>
                )}

                <Separator className="my-6 bg-[#1C1C22]" />

                {/* Promo Code Section */}
                <div className="mb-6">
                  <Label className="text-sm text-zinc-400 mb-2 block">{t('checkout.promoCode')}</Label>
                  {isDrinksOnly ? (
                    <p className="text-xs text-zinc-500 italic" data-testid="drinks-only-promo-notice">
                      {t('checkout.drinksOnlyNoPromo')}
                    </p>
                  ) : appliedPromo ? (
                    <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="font-medium text-green-500">{appliedPromo.code}</span>
                        <span className="text-sm text-zinc-400">(-{formatPrice(appliedPromo.discount_amount)})</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-zinc-400 hover:text-red-400"
                        onClick={removePromoCode}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                        <Input
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder={t('checkout.enterCode')}
                          className="pl-10 bg-[#0B0B0C] border-[#27272A] uppercase"
                          data-testid="promo-code-input"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={applyPromoCode}
                        disabled={promoLoading}
                        className="border-[#FFC107] text-[#FFC107] hover:bg-[#FFC107]/10"
                        data-testid="apply-promo-btn"
                      >
                        {promoLoading ? '...' : t('checkout.apply')}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-zinc-400">
                    <span>{t('cart.subtotal')}</span>
                    <span>{formatPrice(cartTotal)}</span>
                  </div>
                  {appliedPromo && (
                    <div className="flex justify-between text-green-500">
                      <span>{t('checkout.discount')} ({appliedPromo.discount_type === 'percentage' ? `${appliedPromo.discount_value}%` : t('checkout.fixed')})</span>
                      <span>-{formatPrice(discountAmount)}</span>
                    </div>
                  )}
                  {selectedReward && (
                    <div className="flex justify-between text-[#FFC107]">
                      <span className="flex items-center gap-1"><Gift className="h-3.5 w-3.5" /> {t('loyalty.freeReward')}</span>
                      <span>{t('checkout.free')}</span>
                    </div>
                  )}
                  {orderType === 'delivery' && (
                    <div className="flex justify-between text-zinc-400">
                      <span>{t('cart.deliveryFee')}</span>
                      {checkingDelivery ? (
                        <span className="text-zinc-500">...</span>
                      ) : deliveryFee === 0 ? (
                        <span className="text-green-500">{t('checkout.free')}</span>
                      ) : (
                        <span className="text-amber-400">CHF {deliveryFee.toFixed(2)}</span>
                      )}
                    </div>
                  )}
                  <Separator className="bg-[#1C1C22]" />
                  <div className="flex justify-between text-xl font-bold">
                    <span>{t('cart.total')}</span>
                    <span className="text-[#FFC107]">{formatPrice(finalTotal + (orderType === 'delivery' ? deliveryFee : 0))}</span>
                  </div>
                </div>

                {/* Loyalty Stamp Card */}
                {isAuthenticated && loyaltyStatus && (
                  <div className="mt-6 pt-4 border-t border-[#27272A]">
                    <StampCard
                      stamps={loyaltyStatus.stamps}
                      stampsRequired={loyaltyStatus.stamps_required}
                      rewardAvailable={loyaltyStatus.reward_available}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
  </>
);

export default OrderSummarySection;
